// One status vocabulary for internal tasks and clinic orders. Mirrors the
// backend rule in backend/api/task_review.py — keep them in step.
const NORMALIZE = {
  unassigned: "unassigned",
  pending: "assigned",
  open: "assigned",
  in_progress: "in_progress",
  pending_review: "pending_review",
  completed: "completed",
  done: "completed",
  cancelled: "cancelled",
  rejected: "rejected",
};

export function normalizeStatus(raw) {
  return NORMALIZE[raw] || "unassigned";
}

export const STATUS_META = {
  unassigned: { label: "Unassigned", colorScheme: "gray", tw: "bg-slate-100 text-slate-600 ring-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-600" },
  assigned: { label: "Assigned", colorScheme: "purple", tw: "bg-purple-50 text-purple-700 ring-purple-300 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-500/30" },
  in_progress: { label: "In progress", colorScheme: "blue", tw: "bg-sky-50 text-sky-700 ring-sky-300 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30" },
  pending_review: { label: "Pending review", colorScheme: "orange", tw: "bg-amber-50 text-amber-800 ring-amber-300 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30" },
  completed: { label: "Completed", colorScheme: "green", tw: "bg-emerald-50 text-emerald-700 ring-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30" },
  cancelled: { label: "Cancelled", colorScheme: "gray", tw: "bg-slate-100 text-slate-500 ring-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-600" },
  rejected: { label: "Rejected", colorScheme: "red", tw: "bg-red-50 text-red-700 ring-red-300 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30" },
};

export const ACTION_LABELS = {
  complete: "Mark done",
  confirm: "Confirm",
  send_back: "Send back",
  reopen: "Reopen",
  start: "Start",
  stop: "Back to open",
  reject: "Reject",
  reassign: "Reassign",
  cancel: "Cancel task",
  unassign: "Unassign",
};

// Actions that open a window first: who it goes to and/or a message.
export const DECISION_ACTIONS = ["reject", "reassign", "cancel", "unassign"];

export function isAssigner(item, userId) {
  return !item.assignedBy || item.assignedBy === userId;
}

// Edit (title, description, due date) is for the person recorded as having
// assigned the task, until it's completed or cancelled (reopen it first). The
// backend checks the same: internal_task_edit in backend/api/messaging.py.
// The person who assigned a task or an order can edit it (an order: its text
// and priority) until it's closed.
export function canEditTask(item, userId) {
  return (item?.kind === "task" || item?.kind === "order") && !!userId && item.assignedBy === userId
    && item.status !== "completed" && item.status !== "cancelled";
}

export function availableActions(item, userId) {
  const assigner = isAssigner(item, userId);
  const assignee = !!userId && item.assignedTo === userId;
  const involved = assigner || assignee;
  // Someone else handed it over, so there is someone to hand it back to.
  const handedOver = !!item.assignedBy && item.assignedBy !== item.assignedTo;
  switch (item.status) {
    case "assigned":
    case "in_progress": {
      if (!involved) return [];
      const actions = [];
      // The In progress step is the assignee's own marker (tasks only).
      const ownsStep = item.kind === "task" && assignee;
      if (ownsStep && item.status === "assigned") actions.push("start");
      actions.push("complete");
      if (ownsStep && item.status === "in_progress") actions.push("stop");
      if (assignee && handedOver) actions.push("reject");
      actions.push("reassign");
      if (item.kind === "task" && assigner) actions.push("cancel");
      return actions;
    }
    case "rejected":
      return assigner ? ["reassign", item.kind === "order" ? "unassign" : "cancel"] : [];
    case "pending_review":
      if (assigner) return ["confirm", "send_back"];
      return assignee ? ["reopen"] : [];
    case "completed":
      return involved ? ["reopen"] : [];
    case "cancelled":
      // Only whoever cancelled it (the assigner) can undo that.
      return assigner ? ["reopen"] : [];
    default:
      return [];
  }
}
